'use client';

/**
 * WebQALlmAnswerCard — Reception deeper-synthesis tier.
 *
 * The only Reception card that waits on the LLM. The input card fires the
 * fast tier (`mode='auto'`) first — every other card paints off that in
 * ~1.8s — and, if it came back `llm_status='auto_running'`, auto-fires a
 * background `mode='full'` call (`useDocsStore.llmLoading`). This card
 * branches:
 *  - llmLoading / "auto_running": "Synthesising a deeper answer…" spinner
 *  - "completed":                 render `summary.llm.long`
 *  - "skipped":                   the fast tier was high-confidence — no LLM
 *  - "available_on_request":      a "think harder" affordance (disabled in v1)
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { Loader2, Sparkles } from 'lucide-react';
import { useDocsStore } from '@/lib/stores/docs-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import { QA_SECTION } from './web-qa-chrome';


export function WebQALlmAnswerCard() {
  const T = useCardTypography();
  const response = useDocsStore((s) => s.response);
  const llmLoading = useDocsStore((s) => s.llmLoading);

  if (!response) return null;

  const status = response.llm_status ?? 'skipped';
  const llmText =
    response.summary?.llm?.long ?? response.long_answer ?? '';

  if (status === 'completed' && llmText) {
    return (
      <section className={QA_SECTION}>
        <div className="w-full">
          <div className="rounded-2xl border border-primary/30 bg-card/70 p-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-primary" />
              <div className={cn(T.metadata, 'uppercase tracking-wider text-xs text-primary')}>
                Deeper synthesis
              </div>
            </div>
            <p className={cn(T.body, 'whitespace-pre-line leading-relaxed')}>
              {llmText}
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (llmLoading || status === 'auto_running') {
    return (
      <section className={QA_SECTION}>
        <div className="w-full">
          <div className="rounded-2xl border border-primary/30 bg-card/50 p-6 flex items-center gap-3 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span className={T.body}>Synthesising a deeper answer…</span>
          </div>
        </div>
      </section>
    );
  }

  // status === 'skipped' or 'available_on_request' with no llmText —
  // the substrate says there's nothing deeper to show, so suppress.
  // Re-introduce a "Think harder" affordance here once force_llm is
  // wired into docs.ask.
  return null;
}
